import React, { useState, useEffect } from "react";
import { formatMonthsToYearsMonths } from "../utils/formatters";

const LoanInputSection = ({ emiState }) => {
  const {
    loanAmount, setLoanAmount,
    interestRate, setInterestRate,
    loanPeriod, setLoanPeriod,
    monthlyExtraPaymentInput, setMonthlyExtraPaymentInput,
    setMonthlyExtraPayment,
    rateType, setRateType,
    errors,
  } = emiState;

  const [loanAmountStr, setLoanAmountStr] = useState(String(loanAmount));
  const [interestRateStr, setInterestRateStr] = useState(String(interestRate));
  const [loanPeriodStr, setLoanPeriodStr] = useState(String(loanPeriod));
  const [extraPaymentStr, setExtraPaymentStr] = useState(String(monthlyExtraPaymentInput));

  // Sync string state if numeric state changes externally
  useEffect(() => { if (document.activeElement?.id !== "loanAmount") setLoanAmountStr(String(loanAmount)); }, [loanAmount]);
  useEffect(() => { if (document.activeElement?.id !== "interestRate") setInterestRateStr(String(interestRate)); }, [interestRate]);
  useEffect(() => { if (document.activeElement?.id !== "loanPeriod") setLoanPeriodStr(String(loanPeriod)); }, [loanPeriod]);
  useEffect(() => { if (document.activeElement?.id !== "extraPayment") setExtraPaymentStr(String(monthlyExtraPaymentInput)); }, [monthlyExtraPaymentInput]);

  const handleLoanAmountChange = (e) => {
    const raw = e.target.value;
    setLoanAmountStr(raw);
    setLoanAmount(raw === "" ? 0 : parseFloat(raw) || 0);
  };

  const handleInterestRateChange = (e) => {
    const raw = e.target.value;
    setInterestRateStr(raw);
    setInterestRate(raw === "" ? 0 : parseFloat(raw) || 0);
  };

  const handleLoanPeriodChange = (e) => {
    const raw = e.target.value;
    setLoanPeriodStr(raw);
    setLoanPeriod(raw === "" ? 0 : parseInt(raw) || 0);
  };

  const handleExtraPaymentChange = (e) => {
    const raw = e.target.value;
    setExtraPaymentStr(raw);
    setMonthlyExtraPaymentInput(raw === "" ? 0 : parseFloat(raw) || 0);
  };

  const handleExtraPaymentKeyPress = (e) => {
    if (e.key === "Enter") {
      const val = extraPaymentStr === "" ? 0 : parseFloat(extraPaymentStr) || 0;
      if (val >= 0) setMonthlyExtraPayment(val);
    }
  };

  return (
    <div className="card">
      <div className="card-accent-bar" />
      <h2 className="section-title gradient-title">🏦 Loan Details</h2>

      {/* Loan Amount */}
      <div className="input-group">
        <label htmlFor="loanAmount">Loan Amount (₹)</label>
        <input
          id="loanAmount"
          type="number"
          value={loanAmountStr}
          onChange={handleLoanAmountChange}
          className={`input-field${errors.loanAmount ? " error" : ""}`}
          min="1"
          placeholder="e.g. 500000"
          aria-describedby={errors.loanAmount ? "loanAmount-error" : undefined}
        />
        {errors.loanAmount && (
          <div id="loanAmount-error" className="input-error">⚠ {errors.loanAmount}</div>
        )}
      </div>

      {/* Rate Type Toggle */}
      <div className="input-group">
        <label>Interest Rate Type</label>
        <div className="rate-type-toggle">
          <button
            type="button"
            onClick={() => setRateType("fixed")}
            className={`rate-toggle-btn${rateType === "fixed" ? " active" : ""}`}
            aria-pressed={rateType === "fixed"}
          >
            <span className="rate-toggle-icon">📊</span>
            <div>
              <span className="rate-toggle-text">Fixed Rate</span>
              <span className="rate-toggle-desc">Constant throughout tenure</span>
            </div>
          </button>
          <button
            type="button"
            onClick={() => setRateType("floating")}
            className={`rate-toggle-btn${rateType === "floating" ? " active" : ""}`}
            aria-pressed={rateType === "floating"}
          >
            <span className="rate-toggle-icon">📈</span>
            <div>
              <span className="rate-toggle-text">Floating Rate</span>
              <span className="rate-toggle-desc">Changes over time</span>
            </div>
          </button>
        </div>
      </div>

      {/* Interest Rate */}
      <div className="input-group">
        <label htmlFor="interestRate">
          {rateType === "fixed" ? "Interest Rate (% p.a.)" : "Initial Interest Rate (% p.a.)"}
        </label>
        <input
          id="interestRate"
          type="number"
          value={interestRateStr}
          onChange={handleInterestRateChange}
          step="0.01"
          min="0.01"
          max="100"
          placeholder="e.g. 8.5"
          className={`input-field${errors.interestRate ? " error" : ""}`}
          aria-describedby={errors.interestRate ? "interestRate-error" : undefined}
        />
        {errors.interestRate && (
          <div id="interestRate-error" className="input-error">⚠ {errors.interestRate}</div>
        )}
      </div>

      {/* Loan Period */}
      <div className="input-group">
        <label htmlFor="loanPeriod">Loan Tenure (months)</label>
        <input
          id="loanPeriod"
          type="number"
          value={loanPeriodStr}
          onChange={handleLoanPeriodChange}
          min="1"
          placeholder="e.g. 240"
          className={`input-field${errors.loanPeriod ? " error" : ""}`}
          aria-describedby={errors.loanPeriod ? "loanPeriod-error" : "loanPeriod-hint"}
        />
        {errors.loanPeriod ? (
          <div id="loanPeriod-error" className="input-error">⚠ {errors.loanPeriod}</div>
        ) : (
          loanPeriod > 0 && (
            <div id="loanPeriod-hint" className="input-hint">
              ≈ {formatMonthsToYearsMonths(loanPeriod)}
            </div>
          )
        )}
      </div>

      {/* Monthly Extra Payment */}
      <div className="input-group">
        <label htmlFor="extraPayment">Monthly Extra Payment (₹)</label>
        <input
          id="extraPayment"
          type="number"
          value={extraPaymentStr}
          onChange={handleExtraPaymentChange}
          onKeyPress={handleExtraPaymentKeyPress}
          min="0"
          placeholder="0"
          className={`input-field${errors.monthlyExtraPayment ? " error" : ""}`}
          aria-describedby={errors.monthlyExtraPayment ? "extra-error" : "extra-hint"}
        />
        {errors.monthlyExtraPayment ? (
          <div id="extra-error" className="input-error">⚠ {errors.monthlyExtraPayment}</div>
        ) : (
          <div id="extra-hint" className="input-hint">
            Press Enter to apply
          </div>
        )}
      </div>
    </div>
  );
};

export default LoanInputSection;
